import clsx from "clsx";
import React, { useCallback, useEffect, useRef, useState } from "react";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { useAboveFold } from "web/react/hooks/use-above-fold/use-above-fold";
import { useSmallBreakpoint } from "web/react/hooks/use-breakpoint/use-breakpoint";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import rothko from "web/script/modules/rothko";
import { BrandAdSerializer } from "web/types/serializers";
import styles from "./brand-ad-container.module.css";

type InfoButtonType = "light" | "dark";

const STYLE_TYPE_CLASSES: { readonly [bt in InfoButtonType]: { icon: string; label: string } } = {
    light: { icon: styles.lightInfoIcon, label: styles.lightInfoLabel },
    dark: { icon: styles.darkInfoIcon, label: styles.darkInfoLabel },
};

interface InfoButtonProps {
    sendAnalyticsEvent: (action: string, label: string, area: string) => void;
    styleType: string;
    labelText: string;
}

function InfoButton({ sendAnalyticsEvent, styleType, labelText }: InfoButtonProps): JSX.Element {
    const [isActive, setIsActive] = useState(false);

    const iconClass = STYLE_TYPE_CLASSES[styleType]["icon"];
    const labelClass = STYLE_TYPE_CLASSES[styleType]["label"];
    if (!iconClass || !labelClass) {
        throw new Error(`Unknown type ${styleType}!`);
    }

    function toggleInfo(): void {
        sendAnalyticsEvent("click", "perf_brand_ad_icon", "icon");
        setIsActive(!isActive);
    }

    return (
        <button
            className={clsx(styles.info, {
                [styles.active]: isActive,
            })}
            onClick={toggleInfo}
        >
            <span className={clsx(styles.infoIcon, iconClass)}>{"i"}</span>
            <span className={clsx(styles.infoLabel, labelClass)}>{labelText}</span>
        </button>
    );
}

const ENDPOINT = "modules/brand_ads";

function BrandAdContainer({
    jpg_banner_image: jpgBannerImage,
    link,
    click_url: clickUrl,
    impression_url: impressionUrl,
    mobile_link: mobileLink,
    jpg_mpu_image: jpgMpuImage,
    product_image: productImage,
    product_link: productLink,
    product_description: productDescription,
    product_price: productPrice,
    partner,
    designer_slug: designerSlug,
    personalized_ads: personalizedAdsDirty,
    has_margin: hasMargin,
    product_image_width: productImageWidth,
    product_image_height: productImageHeight,
    product_clickable: productClickable,
    retailer_network: retailerNetwork,
    campaign_id: campaignId,
    flight_id: flightId,
    creative_id: creativeId,
    ad_id: adId,
}: BrandAdSerializer): null | JSX.Element {
    const partnerSlug = partner.toLowerCase().replace(/\s+/g, "-");
    const containerRef = useRef(null);
    const { isAboveFold, isVisible } = useAboveFold(containerRef);
    const [isLoaded, setIsLoaded] = useState(false);

    // as final insurance, don't accidentally send non-booleans to analytics customData
    const personalizedAds = personalizedAdsDirty ? true : false;

    const sendKevelTracking = useCallback(
        (isClick: boolean): void => {
            const type = "pba";
            const url = isClick ? clickUrl : impressionUrl;
            if (url) {
                const clicked = isClick ? "true" : "false";
                rothko.fetch(ENDPOINT, { url, clicked, type }, { cache: false });
            }
        },
        [clickUrl, impressionUrl]
    );

    const sendAnalyticsEvent = useCallback(
        (action, label, area) => {
            analytics.event(
                "brand_ads", // category
                action,
                label,
                action !== "click", // nonInteraction
                {
                    partner: partnerSlug,
                    designer_slug: designerSlug,
                    personalizedAds: personalizedAds,
                }, // customData
                "", // subType
                {}, // checkoutData
                [
                    // itemData
                    {
                        item_type: "ad_brand",
                        retailer_network: retailerNetwork,
                        campaign_id: String(campaignId),
                        flight_id: String(flightId),
                        creative_id: String(creativeId),
                        ad_id: String(adId),
                        area,
                        above_fold: isAboveFold,
                    },
                ]
            );
        },
        [
            partnerSlug,
            designerSlug,
            personalizedAds,
            retailerNetwork,
            campaignId,
            flightId,
            creativeId,
            adId,
            isAboveFold,
        ]
    );

    useEffect(() => {
        if (!isLoaded) {
            sendAnalyticsEvent("loaded", "perf_brand_ad", "no interaction");
            setIsLoaded(true);
        }
    }, [isLoaded, sendAnalyticsEvent]);

    useEffect(() => {
        if (isVisible) {
            sendKevelTracking(false);
            sendAnalyticsEvent("impression", "perf_brand_ad", "no interaction");
        }
    }, [isVisible, sendKevelTracking, sendAnalyticsEvent]);

    function onProductHeroClick(): void {
        sendKevelTracking(true);
        sendAnalyticsEvent("click", "perf_brand_product_image", "product image");
    }

    function onMainButtonClick(): void {
        sendKevelTracking(true);
        sendAnalyticsEvent("click", "perf_brand_ad_banner", "banner");
    }

    const partnerLabel = (
        <>
            <SVGIcon name="hyperlink" />
            <span className={styles.textWithIcon}>{partner}</span>
        </>
    );

    // Note for future changes: If something regarding media query in which we show this element or the img source changes please update `base_page.jinja` as well since we are preloading this conditionally.
    const productHeroClickable = (
        <a
            className={styles.partnerProductLink}
            href={productLink}
            target="_blank"
            onClick={onProductHeroClick}
            rel="noreferrer"
        >
            <img
                className={clsx(styles.containerImage, styles.partnerProductImage)}
                src={productImage}
                alt={productDescription ?? undefined}
                width={productImageWidth}
                height={productImageHeight}
            />
            <div className={styles.productDetailsContainer}>
                <div className={styles.productDetails}>{productDescription}</div>
                {productPrice && (
                    <div className={styles.productPrice}>
                        <SVGIcon name="truck" />
                        <span className={styles.textWithIcon}>{productPrice}</span>
                    </div>
                )}
                <div className={styles.productPartnerLabel}>{partnerLabel}</div>
            </div>
        </a>
    );

    const partnerProductNotClickable = (
        <>
            <img
                className={clsx(styles.containerImage, styles.partnerProductImage)}
                src={productImage}
                alt={productDescription ?? undefined}
                width={productImageWidth}
                height={productImageHeight}
            />
            <a
                className={styles.partnerProductLink}
                href={productLink}
                target="_blank"
                onClick={onProductHeroClick}
                rel="noreferrer"
            >
                <div className={styles.productDetailsContainer}>
                    <div className={styles.productPartnerLabel}>{partnerLabel}</div>
                </div>
            </a>
        </>
    );

    const [isClient, setIsClient] = useState(false);

    useEffect(() => {
        setIsClient(true);
    }, []);

    // key forces component to re-render on the client for the correct href.
    const mainButtonKey = isClient ? "client" : "server";
    const mainButtonHref = useSmallBreakpoint() ? link : mobileLink;

    const mainButton = (
        <a
            key={mainButtonKey}
            href={mainButtonHref}
            className="main-tracked-button"
            target="_blank"
            onClick={onMainButtonClick}
            rel="noreferrer"
        >
            {/* Note for future changes: If something regarding the media queries in images load or the picture sources change please update `base_page.jinja` as well since we are preloading them conditionally. */}
            <picture>
                <source media="(min-width: 576px)" srcSet={jpgBannerImage} />
                <source media="(max-width: 575px)" srcSet={jpgMpuImage} />
                <img className={styles.containerImage} src={jpgBannerImage} alt="test banner" />
            </picture>
            <div className={styles.bannerPartnerLabel}>{partnerLabel}</div>
        </a>
    );
    const featuredProductCard = (
        <div className={styles.partnerProductContainer}>
            {productClickable ? productHeroClickable : partnerProductNotClickable}
            <InfoButton
                sendAnalyticsEvent={sendAnalyticsEvent}
                styleType="light"
                labelText={gettext("feed.header.featured_label")} // Featured
            />
        </div>
    );
    return (
        <div
            ref={containerRef}
            className={clsx(styles.container, {
                [styles.hasMargin]: hasMargin,
            })}
        >
            {featuredProductCard}
            <div className={styles.bannerContainer}>
                {mainButton}
                <InfoButton
                    sendAnalyticsEvent={sendAnalyticsEvent}
                    styleType="dark"
                    labelText={gettext("feed.header.sponsored_label")} // Sponsored
                />
            </div>
        </div>
    );
}

export default BrandAdContainer;
